import { SensorType, useLogger } from 'common';
import React, { useEffect, useState } from 'react';
import { YStack } from 'tamagui';

import { WaterQualityCard, HVACCard, AirQualityCard, EnergyCard } from './components';
import { LoadingOverlay } from '../../components/common/LoadingOverlay';
import { FourCardLayout } from '../../components/layouts/FourCardLayout';
import { useAppSync } from '../../contexts/AppSyncContext';
import { ElectricalAlertProvider } from '../../contexts/ElectricalAlertContext';
import { HumidityAlertProvider } from '../../contexts/HumidityAlertContext';
import { WaterAlertProvider } from '../../contexts/WaterAlertContext';

type HubConfig = {
  Water?: { Enabled: boolean };
  HVAC?: { Enabled: boolean };
  Air?: { Enabled: boolean };
  Electrical?: { Enabled: boolean };
};

const sensorTypeToConfigMap = {
  [SensorType.WATER_QUALITY]: 'Water',
  [SensorType.HVAC]: 'HVAC',
  [SensorType.AIR_QUALITY]: 'Air',
  [SensorType.POWER]: 'Electrical',
};

const cardComponentMap = {
  [SensorType.WATER_QUALITY]: (
    <WaterAlertProvider>
      <HumidityAlertProvider>
        <WaterQualityCard />
      </HumidityAlertProvider>
    </WaterAlertProvider>
  ),
  [SensorType.HVAC]: <HVACCard />,
  [SensorType.AIR_QUALITY]: <AirQualityCard />,
  [SensorType.POWER]: (
    <ElectricalAlertProvider>
      <EnergyCard />
    </ElectricalAlertProvider>
  ),
};

export const Home: React.FC = () => {
  const { hub } = useAppSync();
  const [hubConfig, setHubConfig] = useState<HubConfig>({});
  const logger = useLogger('Home');

  useEffect(() => {
    if (hub?.config) {
      try {
        const config = JSON.parse(hub.config);
        setHubConfig(config);
      } catch (error) {
        logger.error('Error parsing hub config:', { error });
        setHubConfig({});
      }
    } else {
      setHubConfig({});
    }
  }, [hub]);

  if (!hub) {
    return <LoadingOverlay isVisible={true} />;
  }

  const cardOrder = [
    SensorType.WATER_QUALITY,
    SensorType.HVAC,
    SensorType.AIR_QUALITY,
    SensorType.POWER,
  ];

  const enabledCards = cardOrder
    .filter((type) => {
      const configKey = sensorTypeToConfigMap[type];
      return hubConfig && hubConfig[configKey as keyof HubConfig]?.Enabled === true;
    })
    .map((type) => cardComponentMap[type]);

  return (
    <YStack flex={1} padding="$5" justifyContent="center">
      <FourCardLayout
        card1={enabledCards[0]}
        card2={enabledCards[1]}
        card3={enabledCards[2]}
        card4={enabledCards[3]}
      />
    </YStack>
  );
};
